import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { Recipe, Timer } from '@/types';
import { vibrate } from '@/lib/utils';

interface CookingSession {
  recipeId?: string;
  recipeName: string;
  servings: number;
  originalServings: number;
  recipe?: Recipe;
}

type ViewMode = 'focus' | 'overview';

interface CookingState {
  // Current session
  session: CookingSession | null;
  isSessionActive: boolean;

  // Timers (separate from session for flexibility)
  timers: Timer[];

  // Wall-clock timestamp of last tick (persisted for rehydration catch-up)
  lastTickAt: number | null;

  // Ingredient tracking
  checkedIngredientIds: string[];

  // Step progress tracking (indices into session.recipe.steps)
  completedStepIndices: number[];

  // View mode for cooking session
  viewMode: ViewMode;

  // Actions - Session
  startSession: (session: Partial<CookingSession> & { recipeName: string; servings: number; originalServings: number }) => void;
  endSession: () => void;
  updateServings: (servings: number) => void;

  // Actions - Timers
  addTimer: (timer: Timer) => void;
  updateTimer: (id: string, updates: Partial<Timer>) => void;
  removeTimer: (id: string) => void;
  toggleTimer: (id: string) => void;
  resetTimer: (id: string) => void;

  // Actions - Ingredients
  toggleIngredient: (ingredientId: string) => void;
  resetCheckedIngredients: () => void;

  // Actions - Steps
  toggleStep: (index: number) => void;
  resetCompletedSteps: () => void;

  // Actions - View Mode
  setViewMode: (mode: ViewMode) => void;

  // Computed
  getActiveTimers: () => Timer[];
}

// Timer tick interval
let timerInterval: number | null = null;

// Module-level wall-clock anchor for tick accuracy
let lastTickTimestamp = Date.now();

type TimerStateGetter = () => Pick<CookingState, 'timers' | 'lastTickAt'>;
type TimerStateSetter = (partial: Partial<CookingState>) => void;

function stopTimerTick(): void {
  if (timerInterval !== null) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
}

function tickTimers(get: TimerStateGetter, set: TimerStateSetter): void {
  const { timers } = get();
  const hasRunningTimers = timers.some((timer) => timer.status === 'running');

  if (!hasRunningTimers) {
    stopTimerTick();
    return;
  }

  const now = Date.now();
  const elapsedSeconds = Math.max(1, Math.round((now - lastTickTimestamp) / 1000));
  lastTickTimestamp = now;

  let didChange = false;
  const updatedTimers = timers.map((timer) => {
    if (timer.status !== 'running') return timer;

    const newRemaining = timer.remaining - elapsedSeconds;
    if (newRemaining <= 0) {
      didChange = true;
      if (timer.soundEnabled) playBeep();
      if (timer.vibrationEnabled) vibrate([200, 100, 200]);
      return { ...timer, remaining: 0, status: 'completed' as const };
    }

    didChange = true;
    return { ...timer, remaining: newRemaining };
  });

  if (didChange) {
    set({ timers: updatedTimers, lastTickAt: now });
  }
}

/** Catch up timers after a gap (tab backgrounded, page rehydrated). */
function catchUpTimers(get: TimerStateGetter, set: TimerStateSetter): void {
  const { timers, lastTickAt } = get();
  if (!lastTickAt) return;

  const now = Date.now();
  const elapsedSeconds = Math.round((now - lastTickAt) / 1000);
  if (elapsedSeconds <= 1) return; // Nothing meaningful to catch up

  const hasRunningTimers = timers.some((t) => t.status === 'running');
  if (!hasRunningTimers) return;

  const updatedTimers = timers.map((timer) => {
    if (timer.status !== 'running') return timer;

    const newRemaining = timer.remaining - elapsedSeconds;
    if (newRemaining <= 0) {
      if (timer.soundEnabled) playBeep();
      if (timer.vibrationEnabled) vibrate([200, 100, 200]);
      return { ...timer, remaining: 0, status: 'completed' as const };
    }

    return { ...timer, remaining: newRemaining };
  });

  lastTickTimestamp = now;
  set({ timers: updatedTimers, lastTickAt: now });
}

function reconcileTimerTick(get: TimerStateGetter, set: TimerStateSetter): void {
  // Catch up any elapsed time from persistence gap
  catchUpTimers(get, set);

  const hasRunningTimers = get().timers.some((timer) => timer.status === 'running');

  if (hasRunningTimers && timerInterval === null) {
    lastTickTimestamp = Date.now();
    timerInterval = window.setInterval(() => tickTimers(get, set), 1000);
    return;
  }

  if (!hasRunningTimers) {
    stopTimerTick();
  }
}

export const useCookingStore = create<CookingState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        session: null,
        isSessionActive: false,
        timers: [],
        lastTickAt: null,
        checkedIngredientIds: [],
        completedStepIndices: [],
        viewMode: 'focus' as ViewMode,
        
        // Start a cooking session
        startSession: (sessionData) => {
          set({
            session: {
              recipeId: sessionData.recipeId,
              recipeName: sessionData.recipeName,
              servings: sessionData.servings,
              originalServings: sessionData.originalServings,
              recipe: sessionData.recipe,
            },
            isSessionActive: true,
          });
        },
        
        // Update servings count
        updateServings: (servings) => {
          set((state) => ({
            session: state.session ? { ...state.session, servings: Math.max(1, servings) } : null,
          }));
        },

        // End the current session
        endSession: () => {
          set({ session: null, isSessionActive: false, timers: [], lastTickAt: null, checkedIngredientIds: [], completedStepIndices: [], viewMode: 'focus' });
          stopTimerTick();
        },
        
        // Add timer
        addTimer: (timer) => {
          set((state) => ({
            timers: [...state.timers, timer],
          }));
          reconcileTimerTick(get, (partial) => set(partial));
        },
        
        // Update timer
        updateTimer: (id, updates) => {
          set((state) => ({
            timers: state.timers.map((t) =>
              t.id === id ? { ...t, ...updates } : t
            ),
          }));
          reconcileTimerTick(get, (partial) => set(partial));
        },
        
        // Remove timer
        removeTimer: (id) => {
          set((state) => ({
            timers: state.timers.filter((t) => t.id !== id),
          }));
          reconcileTimerTick(get, (partial) => set(partial));
        },
        
        // Toggle timer (start/pause)
        toggleTimer: (id) => {
          const { timers } = get();
          const timer = timers.find((t) => t.id === id);
          if (!timer) return;

          if (timer.status === 'running') {
            get().updateTimer(id, { status: 'paused' });
            return;
          }

          if (timer.status === 'completed') {
            // Sync wall-clock anchor when restarting a timer
            lastTickTimestamp = Date.now();
            set({ lastTickAt: Date.now() });
            get().updateTimer(id, { status: 'running', remaining: timer.duration });
            return;
          }

          // Sync wall-clock anchor when starting a timer
          lastTickTimestamp = Date.now();
          set({ lastTickAt: Date.now() });
          get().updateTimer(id, { status: 'running' });
        },
        
        // Reset timer
        resetTimer: (id) => {
          const { timers } = get();
          const timer = timers.find((t) => t.id === id);
          if (!timer) return;

          get().updateTimer(id, {
            status: 'idle',
            remaining: timer.duration,
          });
        },

        // Toggle ingredient checked state
        toggleIngredient: (ingredientId) => {
          set((state) => {
            const isChecked = state.checkedIngredientIds.includes(ingredientId);
            return {
              checkedIngredientIds: isChecked
                ? state.checkedIngredientIds.filter((id) => id !== ingredientId)
                : [...state.checkedIngredientIds, ingredientId],
            };
          });
        },

        // Reset all checked ingredients
        resetCheckedIngredients: () => {
          set({ checkedIngredientIds: [] });
        },

        // Toggle step completed state
        toggleStep: (index) => {
          set((state) => {
            const isCompleted = state.completedStepIndices.includes(index);
            return {
              completedStepIndices: isCompleted
                ? state.completedStepIndices.filter((i) => i !== index)
                : [...state.completedStepIndices, index],
            };
          });
        },

        // Reset all completed steps
        resetCompletedSteps: () => {
          set({ completedStepIndices: [] });
        },

        // Set view mode
        setViewMode: (mode) => {
          set({ viewMode: mode });
        },

        // Get active timers
        getActiveTimers: () => {
          const { timers } = get();
          return timers.filter((t) => t.status === 'running' || t.status === 'paused');
        },
      }),
      {
        name: 'cooking-session',
        partialize: (state) => ({
          session: state.session,
          isSessionActive: state.isSessionActive,
          timers: state.timers,
          lastTickAt: state.lastTickAt,
          checkedIngredientIds: state.checkedIngredientIds,
          completedStepIndices: state.completedStepIndices,
          viewMode: state.viewMode,
        }),
        onRehydrateStorage: () => (state, error) => {
          if (error || !state) return;
          reconcileTimerTick(
            () => useCookingStore.getState(),
            (partial) => useCookingStore.setState(partial)
          );
        },
      }
    ),
    { name: 'cooking-store' }
  )
);

// Catch up timers when tab becomes visible again
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      catchUpTimers(
        () => useCookingStore.getState(),
        (partial) => useCookingStore.setState(partial)
      );
      // Re-sync module-level anchor after catch-up
      lastTickTimestamp = Date.now();
    }
  });
}

// Simple beep function with browser compatibility
function playBeep() {
  try {
    // Handle vendor prefixes for older browsers
    const AudioContextClass =
      window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

    if (!AudioContextClass) {
      console.warn('AudioContext not supported in this browser');
      return;
    }

    const audioContext = new AudioContextClass();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (e) {
    console.error('Could not play beep:', e);
  }
}
